import { useState, useEffect } from "react";
import { useParams, Outlet, Link, useLocation } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

export interface ReportData {
  email: string;
  numOpens: number;
  numClicks: number;
}

export interface Campaign {
  _id: string;
  subject: string;
  html: string;
}

export interface EmailCampaignReportContext {
  campaign: Campaign;
  reportData: ReportData[];
}

function getActiveTab(pathname: string) {
  if (pathname.endsWith("/code")) return "code";
  if (pathname.endsWith("/preview")) return "preview";
  return "report";
}

export function EmailCampaignReportLayout() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const tab = getActiveTab(location.pathname);

  useEffect(() => {
    async function fetchData() {
      if (!id) return;

      setIsLoading(true);
      try {
        const response = await fetch(`/api/email-campaigns/${id}/report`);

        if (response.ok) {
          const data = await response.json();
          setCampaign(data.campaign);
          setReportData(data.reportData || []);
        } else {
          console.error("Failed to fetch email campaign report");
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();

    return () => {
      clearBreadcrumbs();
    };
  }, [id]);

  useEffect(() => {
    if (campaign && id) {
      setBreadcrumbs([
        { label: "Messages", path: "/messages" },
        { label: "Emails", path: "/messages/emails" },
        { label: campaign.subject, path: `/messages/emails/${id}/report` },
      ]);
    }
  }, [campaign, id]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="p-4 text-muted-foreground">Email campaign not found.</div>
    );
  }

  return (
    <div className="flex flex-col min-h-0 flex-1 p-4">
      <h1 className="text-2xl font-bold mb-4">{campaign.subject}</h1>

      <Tabs value={tab} className="flex flex-col flex-1 min-h-0">
        <TabsList className="mb-4 w-fit">
          <TabsTrigger asChild value="report">
            <Link to={`/messages/emails/${id}/report`}>Report</Link>
          </TabsTrigger>
          <TabsTrigger asChild value="preview">
            <Link to={`/messages/emails/${id}/preview`}>Preview</Link>
          </TabsTrigger>
          <TabsTrigger asChild value="code">
            <Link to={`/messages/emails/${id}/code`}>Code</Link>
          </TabsTrigger>
        </TabsList>

        <Outlet context={{ campaign, reportData } satisfies EmailCampaignReportContext} />
      </Tabs>
    </div>
  );
}
